import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";

import {
  DEVICE_STATUS,
  isCalibrationScene,
  isTutorialScene,
  preserveKnownSubstatus,
} from "../components/new_test_ui/new_test_steps/common/DeviceStatus";
import { getDeviceScene } from "../utils/api-communication/RestApiCli";
import { resolvePolledScene } from "../utils/headsetRebootPending";
import { updateDeviceStatus, updateScene } from "../utils/redux-utils/actions/productActions";
import {
  broadcastScene,
  subscribeBroadcastedScene,
  useSceneLeader,
} from "../utils/scenePollLeader";
import { RootState, store } from "@store/index";
import { InactivityLevel, useInactivityLevel } from "./useInactivityLevel";
import { useNetworkStatus } from "./useNetworkStatus";
import { useTabVisibility } from "./useTabVisibility";

const ACTIVE_INTERVAL_MS = 15_000;
const IDLE1_INTERVAL_MS = 75_000;
const ERROR_BACKOFF_BASE_MS = 15_000;
const ERROR_BACKOFF_CAP_MS = 120_000;
const ERROR_JITTER_FRACTION = 0.2;
export function statusFromScene(
  scene: string | null | undefined,
  isTutorialActive?: boolean,
): number {
  // The scene polling endpoint reports null/empty scene when the headset is gone.
  // Treat that as disconnected, unlike /ws/now where a lastInteraction can still
  // mean a connected-but-idle headset.
  if (!scene) return DEVICE_STATUS.NotConnected;
  if (scene === "Menu") return DEVICE_STATUS.Menu;
  if (isCalibrationScene(scene)) return DEVICE_STATUS.Calibration;
  if (isTutorialActive || isTutorialScene(scene)) return DEVICE_STATUS.Tutorial;
  return DEVICE_STATUS.Test;
}

function intervalFor(level: InactivityLevel): number | null {
  if (level === "active") return ACTIVE_INTERVAL_MS;
  if (level === "idle1") return IDLE1_INTERVAL_MS;
  return null;
}

function backoffMs(errorCount: number): number {
  const exponent = Math.max(0, errorCount - 1);
  const base = Math.min(ERROR_BACKOFF_BASE_MS * 2 ** exponent, ERROR_BACKOFF_CAP_MS);
  const jitter = base * ERROR_JITTER_FRACTION * (Math.random() * 2 - 1);
  return Math.max(1000, Math.round(base + jitter));
}

export interface UseScenePollingOptions {
  enabled?: boolean;
}

export function useScenePolling(
  pid: string | null | undefined,
  options: UseScenePollingOptions = {},
): void {
  const { enabled = true } = options;
  const dispatch = useDispatch();
  const visibility = useTabVisibility();
  const network = useNetworkStatus();
  const inactivityLevel = useInactivityLevel();
  const canPoll =
    enabled &&
    Boolean(pid) &&
    visibility === "visible" &&
    network === "online" &&
    inactivityLevel !== "idle2";
  const isLeader = useSceneLeader(canPoll);

  const etagRef = useRef<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const errorCountRef = useRef(0);
  const lastPidRef = useRef<string | null | undefined>(undefined);

  if (lastPidRef.current !== pid) {
    etagRef.current = null;
    errorCountRef.current = 0;
    lastPidRef.current = pid;
  }

  useEffect(() => {
    if (!enabled || !pid) return undefined;
    return subscribeBroadcastedScene(({ scene, etag, ts }) => {
      etagRef.current = etag ?? etagRef.current;
      dispatch(updateScene(scene ?? "", scene ? new Date(ts) : null));
      const currentStatus = (store.getState() as RootState).product.deviceStatus;
      dispatch(updateDeviceStatus(preserveKnownSubstatus(statusFromScene(scene), currentStatus)));
    }, pid);
  }, [dispatch, enabled, pid]);

  useEffect(() => {
    // Poll regardless of the web app's own WS connection state. A live
    // backend WS doesn't imply the headset is still connected — the headset
    // can drop while our socket stays open, in which case the backend reports
    // scene=null but no scene message is pushed. Polling catches that.
    const shouldPoll = canPoll && isLeader;

    function clearTimer() {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    }
    function abortInFlight() {
      abortRef.current?.abort();
      abortRef.current = null;
    }
    function shutdown() {
      clearTimer();
      abortInFlight();
    }

    if (!shouldPoll) {
      shutdown();
      return shutdown;
    }

    let cancelled = false;

    function scheduleNext(ms: number) {
      clearTimer();
      if (cancelled) return;
      timerRef.current = setTimeout(fire, ms);
    }

    async function fire() {
      if (cancelled) return;
      abortInFlight();
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        const result = await getDeviceScene(pid as string, etagRef.current, controller.signal);
        if (cancelled || controller.signal.aborted) return;
        if (result.etag) etagRef.current = result.etag;
        if (result.status === 200) {
          const scene = resolvePolledScene(pid, result.scene);
          // An empty scene means the headset is gone — pass null so the store
          // keeps the time the headset was last actually seen.
          dispatch(updateScene(scene ?? "", scene ? new Date() : null));
          const currentStatus = (store.getState() as RootState).product.deviceStatus;
          dispatch(
            updateDeviceStatus(preserveKnownSubstatus(statusFromScene(scene), currentStatus)),
          );
          broadcastScene(scene, result.etag, pid as string);
        }
        errorCountRef.current = 0;
        const next = intervalFor(inactivityLevel);
        if (next != null) scheduleNext(next);
      } catch (error: any) {
        if (cancelled) return;
        if (
          error?.name === "CanceledError" ||
          error?.name === "AbortError" ||
          error?.code === "ERR_CANCELED"
        ) {
          return;
        }
        const status = error?.response?.status;
        if (status === 401) {
          shutdown();
          return;
        }
        errorCountRef.current += 1;
        scheduleNext(backoffMs(errorCountRef.current));
      }
    }

    fire();

    return () => {
      cancelled = true;
      shutdown();
    };
  }, [canPoll, dispatch, inactivityLevel, isLeader, pid]);
}
